<template>
  <div class="group-members-table">
    <v-skeleton-loader v-if="loading" type="table-row@5" />

    <v-card v-else-if="!members.length" outlined class="group-members-table__empty">
      <v-icon size="48" color="grey lighten-1">mdi-video-plus-outline</v-icon>
      <div class="subtitle-1 grey--text text--darken-1 mt-2">This group has no videos</div>
      <v-btn small color="primary" depressed class="mt-3" @click="$emit('add-members')">
        <v-icon small left>mdi-plus</v-icon>
        Add videos
      </v-btn>
    </v-card>

    <v-data-table
      v-else
      v-model="selected"
      :headers="headers"
      :items="rows"
      item-key="member_id"
      :show-select="selectable"
      dense
      :items-per-page="25"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      class="group-members-table__table"
      @input="$emit('selection-change', $event)"
    >
      <template #[`item.name`]="{ item }">
        <v-btn x-small text color="primary" class="text-none" @click="open(item)">
          <v-icon x-small left>mdi-open-in-new</v-icon>
          {{ item.display_name }}
        </v-btn>
        <div class="caption grey--text pl-1">
          {{ item.source_type === 'interview' ? 'Interview' : 'Video' }} #{{ item.source_id }}
        </div>
      </template>
      <template #[`item.label`]="{ item }">
        <v-chip
          v-if="item.label_type"
          x-small
          outlined
          :color="item.label_type === 'truthful' ? 'success' : 'error'"
        >
          {{ item.label_type === 'truthful' ? 'Truthful' : 'Lie' }}
        </v-chip>
        <span v-else class="caption grey--text">unlabelled</span>
      </template>
      <template #[`item.resolution`]="{ item }">
        <v-chip
          v-for="chip in item.chips"
          :key="chip.key"
          x-small
          label
          :color="chip.color"
          :outlined="chip.outlined"
          :dark="!chip.outlined"
          class="mr-1 group-members-table__chip"
          :class="`group-members-table__chip--${chip.key}`"
        >
          <v-icon x-small left>{{ chip.icon }}</v-icon>
          {{ chip.text }}
        </v-chip>
      </template>
      <template #[`item.run`]="{ item }">
        <RunVersionChips v-if="item.run" :run="item.run" :source-type="item.source_type" dense />
        <span v-else-if="item.resolved && item.resolved.run_id" class="group-members-table__mono">
          {{ shortRun(item.resolved.run_id) }}
        </span>
        <span v-else class="grey--text">—</span>
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-end">
          <v-btn
            icon
            x-small
            title="Open run"
            :disabled="!item.resolved || !item.resolved.run_id"
            @click="open(item)"
          >
            <v-icon small>mdi-play-circle-outline</v-icon>
          </v-btn>
          <v-menu v-if="!item.pinned_run_id" offset-y :disabled="!succeededRunsOf(item).length">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                v-bind="attrs"
                title="Pin a succeeded run of this group"
                :disabled="!succeededRunsOf(item).length"
                class="group-members-table__pin"
                v-on="on"
              >
                <v-icon small>mdi-pin-outline</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="run in succeededRunsOf(item)"
                :key="run._id || run.run_id"
                @click="$emit('pin', { member: item, run_id: run._id || run.run_id })"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ runLabel(run) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            v-else
            icon
            x-small
            color="info"
            title="Unpin"
            class="group-members-table__unpin"
            @click="$emit('pin', { member: item, run_id: null })"
          >
            <v-icon small>mdi-pin-off</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            title="Remove from group"
            class="group-members-table__remove"
            @click="$emit('remove', item)"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import RunVersionChips from '@/components/analysis/runs/RunVersionChips.vue';
import { memberLabel, resolutionChips, sourceRouteWithRun } from '@/utils/groupResolution';
import { runLabel } from '@/utils/runLabels';

/**
 * Members of an analysis group with their run resolution (M4 contract 5): bucket chips
 * (Pinned / Analysed / Pending / Not analysed / Never run / Pinned run missing, plus Degraded and
 * Not requested), the resolved run's version chips, pin / unpin and remove.
 * Emits `pin` `{ member, run_id|null }`, `remove` (member), `add-members`, `selection-change`.
 */
export default {
  name: 'GroupMembersTable',

  components: { RunVersionChips },

  props: {
    members: { type: Array, default: () => [] },
    /** Runs of the group (ledger docs) used for the pin menu and the version chips. */
    runs: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    selectable: { type: Boolean, default: true },
  },

  data() {
    return { selected: [] };
  },

  computed: {
    headers() {
      return [
        { text: 'Video', value: 'name' },
        { text: 'Label', value: 'label', width: 100, sortable: false },
        { text: 'Resolution', value: 'resolution', sortable: false },
        { text: 'Run', value: 'run', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 130 },
      ];
    },
    runsById() {
      const map = new Map();
      this.runs.forEach((run) => {
        const id = run && (run._id || run.run_id);
        if (id) map.set(String(id), run);
      });
      return map;
    },
    rows() {
      return this.members.map((member) => {
        const resolved = member.resolved || null;
        const runId = resolved && resolved.run_id ? String(resolved.run_id) : null;
        return {
          ...member,
          display_name: memberLabel(member),
          label_type:
            member.label && typeof member.label === 'object'
              ? member.label.label_type
              : member.label || null,
          chips: resolutionChips(resolved),
          run: runId ? this.runsById.get(runId) || null : null,
        };
      });
    },
  },

  methods: {
    runLabel,

    shortRun(runId) {
      return String(runId).slice(0, 8);
    },

    succeededRunsOf(member) {
      return this.runs.filter(
        (run) =>
          run &&
          String(run.status).toUpperCase() === 'SUCCEEDED' &&
          run.source_type === member.source_type &&
          String(run.source_id) === String(member.source_id)
      );
    },

    open(item) {
      const route = sourceRouteWithRun(item, item.resolved ? item.resolved.run_id : null);
      this.$emit('open', item);
      if (route && this.$router) this.$router.push(route);
    },
  },
};
</script>

<style scoped>
.group-members-table__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  text-align: center;
  border-radius: 10px !important;
}
.group-members-table__mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
}
</style>
